import apiClient from "./apiClient";
import axios from "axios"; // Keep axios for FormData requests
import { BACKEND_URL } from "../utils/envConfig";
const API_URL = `/emergency-details`; // Use relative path since apiClient has baseURL
const FULL_API_URL = `${BACKEND_URL}/emergency-details`; // For FormData requests

// export const createEmergency = async (data) => {
//   const response = await axios.post(`${API_URL}/create`, data);
//   return response.data; 
// };

export const createEmergency = async (data) => {
  // If a file is present, send multipart/form-data with top-level fields (no JSON wrapper)
  const file = data?.firFile;
  const isFileLike = file && typeof file === 'object' && ('name' in file || 'size' in file);
  if (isFileLike) {
    const formData = new FormData();
    if (data.emergencyId != null) formData.append('emergencyId', String(data.emergencyId));
    if (data.admissionReason != null) formData.append('admissionReason', String(data.admissionReason));
    if (data.broughtBy != null) formData.append('broughtBy', String(data.broughtBy));
    if (data.broughtType != null) formData.append('broughtType', String(data.broughtType));
    if (data.ambulanceNumber != null) formData.append('ambulanceNumber', String(data.ambulanceNumber));
    if (data.driverName != null) formData.append('driverName', String(data.driverName));
    if (data.firReport != null) formData.append('firReport', String(data.firReport));
    formData.append('firFile', file, file.name || 'fir.pdf');
    
    // For FormData, we need to use axios directly with full URL and add organizationId manually
    // This ensures records are created in organization-specific tables (e.g., 23_emergency_details)
    // instead of the base emergency_details table
    // Get organizationId from localStorage (only if valid)
    const userStr = localStorage.getItem('user');
    let organizationId = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const orgId = user?.organizationId;
        // Only use organizationId if it's a valid positive number
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
        }
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }
    }
    
    // Add organizationId to FormData only if valid (common users won't have it)
    // This ensures backend routes to organization-specific table
    if (organizationId && organizationId > 0) {
      formData.append('organizationId', String(organizationId));
    }
    
    const resp = await axios.post(`${FULL_API_URL}/create`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp.data;
  }
  // Otherwise, fall back to JSON - use apiClient which automatically adds organizationId as query param
  // Remove organizationId from data if it exists (apiClient will add it as query param for POST requests)
  // This ensures the backend routes to the correct organization-specific table
  const { organizationId, ...emergencyDetailsData } = data;
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // This ensures records are created in organization-specific tables (e.g., 23_emergency_details)
  // instead of the base emergency_details table
  const response = await apiClient.post(`${API_URL}/create`, {
    emergencyId: emergencyDetailsData.emergencyId,
    admissionReason: emergencyDetailsData.admissionReason,
    broughtBy: emergencyDetailsData.broughtBy,
    broughtType: emergencyDetailsData.broughtType,
    ambulanceNumber: emergencyDetailsData.ambulanceNumber,
    driverName: emergencyDetailsData.driverName,
    firReport: emergencyDetailsData.firReport,
  });
  return response.data;
};

export const getByEmergencyId = async (emergencyId) => {
  // Get organizationId from localStorage to validate response
  let expectedOrganizationId = null;
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const orgId = user?.organizationId;
      if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
        expectedOrganizationId = Number(orgId);
      }
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
  }
  
  // apiClient will automatically add organizationId as query param via interceptor
  // This ensures data is fetched from organization-specific table (e.g., 23_emergency_details)
  // instead of the base emergency_details table
  const response = await apiClient.get(`${API_URL}/${emergencyId}`);
  const data = response.data;
  
  // Validate that the returned record belongs to the expected organization
  // This prevents showing data from wrong organization or base table
  if (expectedOrganizationId && data) {
    const returnedOrgId = data.organizationId != null ? Number(data.organizationId) : null;
    
    // If we expect an organization but got null, it means backend returned base table data
    if (returnedOrgId === null) {
      console.error(
        `[EmergencyDetailsService] ⚠️ Organization mismatch: Expected organization ${expectedOrganizationId}, but got null (base table data) for emergency ID ${emergencyId}. ` +
        `This indicates the backend is returning data from the base emergency_details table instead of ${expectedOrganizationId}_emergency_details table. ` +
        `Please verify the backend is correctly reading the organizationId query parameter.`
      );
      // Throw an error to prevent showing incorrect data
      throw new Error(
        `Emergency details record not found in organization-specific table. ` +
        `Expected organization ${expectedOrganizationId}, but backend returned data from base table. ` +
        `Please verify the backend endpoint is correctly handling the organizationId query parameter.`
      );
    }
    
    // If we got a different organization ID, that's also an error
    if (returnedOrgId !== expectedOrganizationId) {
      console.error(
        `[EmergencyDetailsService] Organization mismatch: Expected ${expectedOrganizationId}, got ${returnedOrgId} for emergency ID ${emergencyId}. ` +
        `This may indicate the backend is returning data from the wrong organization table.`
      );
      // Throw an error to prevent showing incorrect data
      throw new Error(
        `Emergency details record belongs to a different organization. ` +
        `Expected organization ${expectedOrganizationId}, but record belongs to organization ${returnedOrgId}.`
      );
    }
  }
  
  return data;
};

export default {
  createEmergency,
  getByEmergencyId,
}